import axios, { AxiosResponse } from 'axios';

import {
  fetchAllByIDsPublicAPI,
  fetchAllPublicAPI,
  parsePublicAPIArray,
} from 'root/api/public/requestUtils';
import {
  parseItemResponse,
  parseItemVendorNumResponse,
  parseNuorderBrandResponse,
  parseNuorderOrderResponse,
  parseNuorderProductOrderableResponse,
  parseNuorderProductResponse,
  parsePurchaseLineResponse,
  parsePurchaseResponse,
  parseVendorResponse,
  parseVendorShopResponse,
} from 'root/apps/PurchaseOrder/NuorderOrdering/context/Parsers';
import {
  Configs,
  DefaultSortingPreference,
  Item,
  ItemVendorNum,
  NuorderBrand,
  NuorderOrder,
  NuorderOrderRequest,
  NuorderProduct,
  NuorderProductOrderable,
  Purchase,
  PurchaseLine,
  PurchaseRequest,
  RawItemResponse,
  SortingPreference,
  Vendor,
  VendorShop,
} from 'root/apps/PurchaseOrder/NuorderOrdering/types';
import { datadogLogs } from 'root/datadog/datadogLogs';
import Session from 'shared_modules/Session';

const getPublicRequestConfig = () => {
  const accountID = Session.accountID();
  return {
    baseURL: `/API/V3/Account/${accountID}`,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };
};

const getAdminRequestConfig = () => {
  return {
    baseURL: `/admin`,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };
};

export const fetchPurchase = async (purchaseID: number): Promise<Purchase> => {
  const relations = encodeURIComponent('["Note"]');
  const response = await axios.get(
    `/Order/${purchaseID}.json?load_relations=${relations}`,
    getPublicRequestConfig()
  );
  const purchase = response.data.Order;
  try {
    return { ...purchase, ...parsePurchaseResponse(purchase) };
  } catch (error) {
    throw new Error(`Failed to parse purchase: ${error.message}`);
  }
};

export const fetchPurchaseLines = async (purchaseID: number): Promise<PurchaseLine[]> => {
  const url = `/OrderLine.json?orderID=${purchaseID}`;
  const purchaseLines = await fetchAllPublicAPI<PurchaseLine>(
    'OrderLine',
    url,
    getPublicRequestConfig()
  );
  try {
    return purchaseLines.map((line: PurchaseLine) => ({
      ...line,
      ...parsePurchaseLineResponse(line),
    }));
  } catch (error) {
    throw new Error(`Failed to parse purchase lines: ${error.message}`);
  }
};

export const fetchItems = async (itemIDs: number[]): Promise<Item[]> => {
  if (!itemIDs.length) {
    return [];
  }
  const relations = encodeURIComponent('["ItemVendorNums"]');
  const url = `/Item.json?itemID=IN,%s&load_relations=${relations}`;
  const items = await fetchAllByIDsPublicAPI<RawItemResponse>(
    itemIDs,
    'Item',
    url,
    getPublicRequestConfig()
  );
  try {
    return items
      .map((rawItem: RawItemResponse): Item => {
        const item = rawItem as Item;
        item.ItemVendorNums = parsePublicAPIArray(rawItem.ItemVendorNums?.ItemVendorNum);
        return item;
      })
      .map((item: Item) => ({
        ...item,
        ...parseItemResponse(item),
      }));
  } catch (error) {
    throw new Error(`Failed to parse items: ${error.message}`);
  }
};

export const fetchVendor = async (vendorID: number): Promise<Vendor> => {
  const response = await axios.get(`/Vendor/${vendorID}.json`, getPublicRequestConfig());
  const vendor = response.data.Vendor;
  try {
    return { ...vendor, ...parseVendorResponse(vendor) };
  } catch (error) {
    throw new Error(`Failed to parse vendor: ${error.message}`);
  }
};

export const fetchVendorShop = async (
  vendorID: number,
  shopID: number
): Promise<VendorShop | null> => {
  try {
    const response = await axios.get(
      `/vendor-shop/vendor/${vendorID}/shop/${shopID}`,
      getAdminRequestConfig()
    );
    if (!response.data.data) {
      return null;
    }
    const vendorShop = response.data.data;
    try {
      return { ...vendorShop, ...parseVendorShopResponse(vendorShop) };
    } catch (error) {
      throw new Error(`Failed to parse vendor shop: ${error.message}`);
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null; // If the vendor shop does not exist, return null
    }
    throw error;
  }
};

export const fetchNuorderOrder = async (
  b2bSellerUID: string,
  b2bOrderUID: string
): Promise<NuorderOrder> => {
  const response = await axios.get(
    `/nuorder/merchant/v1/brands/${b2bSellerUID}/orders/${b2bOrderUID}`,
    getAdminRequestConfig()
  );
  const nuorderOrder = response.data;
  try {
    return {
      ...nuorderOrder,
      ...parseNuorderOrderResponse(nuorderOrder),
    };
  } catch (error) {
    throw new Error(`Failed to parse NuORDER order: ${error.message}`);
  }
};

export const fetchNuorderBrand = async (brandID: string): Promise<NuorderBrand> => {
  const response = await axios.get(
    `/nuorder/merchant/v1/context/brands/${brandID}`,
    getAdminRequestConfig()
  );
  const nuorderBrand = response.data;
  try {
    return {
      ...nuorderBrand,
      ...parseNuorderBrandResponse(nuorderBrand),
    };
  } catch (error) {
    throw new Error(`Failed to parse NuORDER brand: ${error.message}`);
  }
};

export const fetchNuorderProductByUPC = async (
  b2bSellerUID: string,
  UPCs: string[]
): Promise<NuorderProduct[]> => {
  if (!UPCs.length) {
    return [];
  }
  const requestBody = {
    filter: {
      and: {
        filters: [
          {
            in: {
              field: 'upc',
              values: UPCs.map((upc) => ({
                stringValue: upc,
              })),
            },
          },
          {
            eq: {
              field: 'seller_id',
              value: {
                stringValue: b2bSellerUID,
              },
            },
          },
        ],
      },
    },
    withInventoryValues: false,
    ignoreAvailabilityCheck: false,
    showNotXQualifiedProducts: true,
  };
  const response = await axios.post(
    `/nuorder/catalog/v1/coreproduct/search`,
    requestBody,
    getAdminRequestConfig()
  );
  if (!response.data.data) {
    return [];
  }
  try {
    return response.data.data.map((nuorderProduct: NuorderProduct) => ({
      ...nuorderProduct,
      ...parseNuorderProductResponse(nuorderProduct),
    }));
  } catch (error) {
    throw new Error(`Failed to parse NuORDER products by UPC: ${error.message}`);
  }
};

export const fetchNuorderAvailability = async (
  b2bSellerUID: string,
  uuids: string[]
): Promise<NuorderProductOrderable[]> => {
  if (!uuids.length) {
    return [];
  }
  const requestBody = {
    filter: {
      and: {
        filters: [
          {
            in: {
              field: 'id',
              values: uuids.map((uuid) => ({
                stringValue: uuid,
              })),
            },
          },
          {
            eq: {
              field: 'seller_id',
              value: {
                stringValue: b2bSellerUID,
              },
            },
          },
        ],
      },
    },
    withInventoryValues: true,
    ignoreAvailabilityCheck: false,
    showNotXQualifiedProducts: true,
  };
  const response = await axios.post(
    `/nuorder/catalog/v1/coreproduct/search`,
    requestBody,
    getAdminRequestConfig()
  );
  if (!response.data.data) {
    return [];
  }
  try {
    return response.data.data.map((nuorderProduct: NuorderProductOrderable) => ({
      ...nuorderProduct,
      ...parseNuorderProductOrderableResponse(nuorderProduct),
    }));
  } catch (error) {
    throw new Error(`Failed to parse NuORDER orderable products: ${error.message}`);
  }
};

export const fetchConfigs = async (): Promise<Configs> => {
  const [b2bWebServiceBaseURI] = await Promise.all([
    window.lsCloud.Config.access('b2b_web_service_base_uri'),
  ]);
  return {
    b2bWebServiceBaseURI: b2bWebServiceBaseURI || '',
  };
};

export const fetchSortingPreference = async (): Promise<SortingPreference> => {
  const [sortKey, sortDirection] = await Promise.all([
    window.lsCloud.UserPreferences.get('listing', 'purchase.nuorder_ordering', 'sortKey'),
    window.lsCloud.UserPreferences.get('listing', 'purchase.nuorder_ordering', 'sortDirection'),
  ]);
  return {
    sortKey: (sortKey as SortingPreference['sortKey']) || DefaultSortingPreference.sortKey,
    sortDirection:
      (sortDirection as SortingPreference['sortDirection']) ||
      DefaultSortingPreference.sortDirection,
  };
};

export const updateSortingPreference = async (
  preference: SortingPreference
): Promise<SortingPreference> => {
  try {
    await window.lsCloud.UserPreferences.bulkSave([
      {
        type: 'listing',
        component: 'purchase.nuorder_ordering',
        name: 'sortKey',
        value: preference.sortKey,
      },
      {
        type: 'listing',
        component: 'purchase.nuorder_ordering',
        name: 'sortDirection',
        value: preference.sortDirection,
      },
    ]);
  } catch (error) {
    // silently fail
    datadogLogs.logger.error(`Failed to update sorting preference: ${error.message}`, {}, error);
  }
  // Optimistic return. We do not want to revert UI state if the update failed.
  return preference;
};

export const updatePurchase = async (
  purchaseID: number,
  requestBody: Partial<PurchaseRequest>
): Promise<Purchase> => {
  const response = await axios.put(
    `/Order/${purchaseID}.json`,
    requestBody,
    getPublicRequestConfig()
  );
  if (!response.data.Order) {
    throw new Error(`Update purchase response is empty.`);
  }
  try {
    return { ...response.data.Order, ...parsePurchaseResponse(response.data.Order) };
  } catch (error) {
    throw new Error(`Failed to parse updated purchase: ${error.message}`);
  }
};

export const updateItem = async (itemID: number, requestBody: Partial<Item>): Promise<Item> => {
  const response = await axios.put(`/Item/${itemID}.json`, requestBody, getPublicRequestConfig());
  if (!response.data.Item) {
    throw new Error(`Update item response is empty.`);
  }
  try {
    const rawItem = response.data.Item as RawItemResponse;
    const updatedItem = rawItem as Item;
    updatedItem.ItemVendorNums = parsePublicAPIArray(rawItem.ItemVendorNums?.ItemVendorNum);
    return { ...updatedItem, ...parseItemResponse(updatedItem) };
  } catch (error) {
    throw new Error(`Failed to parse updated item: ${error.message}`);
  }
};

export const updateItemVendorNum = async (
  itemID: number,
  vendorID: number,
  num: string,
  b2bCatalogUUID?: string
): Promise<ItemVendorNum | null> => {
  const requestBody = {
    vendorNumber: {
      value: num,
      b2bCatalogUUID,
    },
  };
  const response = await axios.put(
    `/vendor-numbers/${vendorID}/${itemID}`,
    requestBody,
    getAdminRequestConfig()
  );
  if (!response.data.data) {
    return null;
  }
  try {
    return {
      ...response.data.data,
      ...parseItemVendorNumResponse(response.data.data),
    };
  } catch (error) {
    throw new Error(`Failed to parse UpdateItemVendorNumResponse: ${error.message}`);
  }
};

export const updatePurchaseLine = async (
  purchaseLineID: number,
  requestBody: Partial<PurchaseLine>
): Promise<PurchaseLine> => {
  const response = await axios.put(
    `/OrderLine/${purchaseLineID}.json`,
    requestBody,
    getPublicRequestConfig()
  );
  if (!response.data.OrderLine) {
    throw new Error(`Update purchase line response is empty.`);
  }
  try {
    return { ...response.data.OrderLine, ...parsePurchaseLineResponse(response.data.OrderLine) };
  } catch (error) {
    throw new Error(`Failed to parse updated purchase line: ${error.message}`);
  }
};

export const deletePurchaseLine = async (purchaseLineID: number): Promise<void> => {
  await axios.delete(`/OrderLine/${purchaseLineID}.json`, getPublicRequestConfig());
};

export const getDataFromNuorderWriteResponse = (response: AxiosResponse): any => {
  let data = response.data;
  // NuORDER Order create return as an array
  if (Array.isArray(data)) {
    if (!data.length) return null;
    data = data[0];
  }
  return data;
};

export const createNuorderOrder = async (
  b2bSellerUID: string,
  requestBody: NuorderOrderRequest
): Promise<NuorderOrder> => {
  const response = await axios.post(
    `/nuorder/merchant/v1/brands/${b2bSellerUID}/orders`,
    requestBody,
    getAdminRequestConfig()
  );
  const data = getDataFromNuorderWriteResponse(response);
  if (!data) {
    throw new Error('Create Nuorder Order response is empty.');
  }
  try {
    return {
      ...data,
      ...parseNuorderOrderResponse(data),
    };
  } catch (error) {
    throw new Error(`Failed to parse createNuorderOrder response: ${error.message}`);
  }
};

export const updateNuorderOrder = async (
  b2bSellerUID: string,
  b2bOrderUID: string,
  requestBody: Partial<NuorderOrderRequest>
): Promise<NuorderOrder> => {
  const response = await axios.put(
    `/nuorder/merchant/v1/brands/${b2bSellerUID}/orders/${b2bOrderUID}`,
    requestBody,
    getAdminRequestConfig()
  );
  const data = getDataFromNuorderWriteResponse(response);
  if (!data) {
    throw new Error('Update Nuorder Order response is empty.');
  }
  try {
    return {
      ...data,
      ...parseNuorderOrderResponse(data),
    };
  } catch (error) {
    throw new Error(`Failed to parse updateNuorderOrder response: ${error.message}`);
  }
};

export const completeNuorderOrder = async (
  b2bSellerUID: string,
  b2bOrderUID: string
): Promise<NuorderOrder> => {
  const response = await axios.put(
    `/nuorder/merchant/v1/brands/${b2bSellerUID}/orders/${b2bOrderUID}/complete`,
    {},
    getAdminRequestConfig()
  );
  const data = getDataFromNuorderWriteResponse(response);
  if (!data) {
    throw new Error('Complete Nuorder Order response is empty.');
  }
  try {
    return {
      ...data,
      ...parseNuorderOrderResponse(data),
    };
  } catch (error) {
    throw new Error(`Failed to parse completeNuorderOrder response: ${error.message}`);
  }
};
